import { gettext } from '../../utils/text';
import { ErrorComponent } from '../../forms/fields/FormField';

declare const DJ_CONST: {
    STATIC_URL: string;
    ENABLED_PAYMENT_PROVIDERS: string[];
};

interface CheckoutOptionsProps {
    payment_provider: string;
    setFieldValue: (
        field: string,
        value: string,
        shouldValidate?: boolean,
    ) => void;
    setFieldTouched: (
        field: string,
        touched?: boolean,
        shouldValidate?: boolean,
    ) => void;
    isFieldInvalid: (field: string) => boolean;
}

interface PaymentProvider {
    value: string;
    label: string;
    logo: string;
    alt: string;
}

interface PaymentOptionProps {
    provider: PaymentProvider;
    name: string;
    checked: boolean;
    onChange: (value: string) => void;
}

const PaymentOption = ({
    provider,
    name,
    checked,
    onChange,
}: PaymentOptionProps) => {
    const itemId = `payment-${provider.value.toLowerCase()}`;

    return (
        <div className="form-check checkout__payment__option">
            <label
                className="form-check-label checkout__payment__label"
                htmlFor={itemId}
            >
                <input
                    id={itemId}
                    name={name}
                    type="radio"
                    className="form-check-input"
                    value={provider.value}
                    checked={checked}
                    onChange={e => onChange(e.target.value)}
                    required
                />
                <div className="form-check__payment-provider">
                    <span>{provider.label}</span>
                    <img
                        src={`${DJ_CONST.STATIC_URL}images/payment_logos/${provider.logo}`}
                        alt=""
                        aria-hidden="true"
                    />
                </div>
            </label>
        </div>
    );
};

const paymentProviders: PaymentProvider[] = [
    {
        value: 'MontonioBank',
        label: gettext('Pay with your bank'),
        logo: 'montonio.png',
        alt: 'Montonio',
    },
    {
        value: 'MontonioCard',
        label: gettext('Card payment'),
        logo: 'visa-mc.png',
        alt: 'Creditcard',
    },
    {
        value: 'swedbank',
        label: gettext('Swedbank'),
        logo: 'swedbank.png',
        alt: 'Swedbank',
    },
    {
        value: 'nordea',
        label: gettext('Luminor'),
        logo: 'luminor.png',
        alt: 'Luminor',
    },
    {
        value: 'seb',
        label: gettext('SEB'),
        logo: 'seb.png',
        alt: 'Seb',
    },
    {
        value: 'lhv',
        label: gettext('LHV'),
        logo: 'lhv.png',
        alt: 'LHV',
    },
    {
        value: 'EveryPay',
        label: gettext('Card payment (EveryPay)'),
        logo: 'visa-mc.png',
        alt: 'Visa',
    },
    {
        // Only enabled on test servers, marks the order paid instantly
        value: 'Mock',
        label: 'Test payment (no real payment)',
        logo: 'visa-mc.png',
        alt: 'Test payment',
    },
];

const CheckoutOptions = ({
    payment_provider,
    setFieldValue,
    setFieldTouched,
    isFieldInvalid,
}: CheckoutOptionsProps) => {
    const enabledProviders = paymentProviders.filter(provider =>
        DJ_CONST.ENABLED_PAYMENT_PROVIDERS.includes(provider.value),
    );

    if (enabledProviders.length === 0) {
        return (
            <div className="alert alert-warning" role="alert">
                {gettext(
                    'No payment methods are currently available. Please contact support.',
                )}
            </div>
        );
    }

    const handleChange = (value: string) => {
        setFieldTouched('payment_provider', true, false);
        setFieldValue('payment_provider', value, true);
    };

    return (
        <fieldset
            className="checkout__payment"
            aria-labelledby="payment-method-legend"
            aria-describedby={
                isFieldInvalid('payment_provider')
                    ? 'payment_provider-error'
                    : undefined
            }
        >
            <legend id="payment-method-legend" className="visually-hidden">
                {gettext('Choose payment method')}
            </legend>
            <ErrorComponent
                name="payment_provider"
                id="payment_provider-error"
            />
            <div className="checkout__payment__group">
                <div className="checkout__payment__links">
                    {enabledProviders.map(provider => (
                        <PaymentOption
                            key={provider.value}
                            provider={provider}
                            name="payment_provider"
                            checked={payment_provider === provider.value}
                            onChange={handleChange}
                        />
                    ))}
                </div>
            </div>
        </fieldset>
    );
};

export default CheckoutOptions;
